import {
  Box,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  withStyles,
} from "@material-ui/core";
import { RouteComponentProps, useLocation, useNavigate } from "@reach/router";
import { useFormik } from "formik";
import { parse } from "query-string";
import { useEffect, useState } from "react";
import {
  IdProvider,
  Status,
  AdminGetUsersQueryParams,
  AdminUserResponse,
  AdminUsersPaginatedResponse,
  SubscriptionPlanKey,
  UserStage,
} from "@authory-packages/core";
import { ComponentLoadingIndicator } from "../components/ComponentLoadingIndicator";
import { Api } from "../lib/api";
import { Layout } from "./Layout";
import { PaginationStyles, StyledTableCell } from "../styles";
import { addDays, format, formatDistance } from "date-fns";
import { getPaymentLabel } from "../utils/getPaymentLabel";
import { getPlanLabel } from "../utils/getPlanLabel";
import { SubscriptionPlanTooltip } from "../components/tooltips/SubscriptionPlanTooltip";
import { PaymentMethodTooltip } from "../components/tooltips/SubscriptionStatusTooltip";

const ROWS_PER_PAGE = 50;

export const showSignupDateCategory = [
  UserStage.AllNew,
  UserStage.NewSignup,
  UserStage.CompletedSignup,
  UserStage.FullCrawlRunning,
  UserStage.ImportFinished,
];

const NO_PLAN = "no_plan";

function enumKeys<O extends object, K extends keyof O = keyof O>(obj: O): K[] {
  return Object.keys(obj).filter((k) => Number.isNaN(+k)) as K[];
}

const subscriptionPlanKey: { value: string; label: string }[] = [
  { value: "", label: "-" },
  { value: NO_PLAN, label: "No Plan" },
];
for (const value of enumKeys(SubscriptionPlanKey)) {
  subscriptionPlanKey.push({
    value: SubscriptionPlanKey[value],
    label: getPlanLabel(SubscriptionPlanKey[value]),
  });
}

const getShownDate = (isNewSignupStage: boolean, user: AdminUserResponse) => {
  if (isNewSignupStage && user.signupDate)
    return format(new Date(user.signupDate), "dd.MM.yyy HH:mm:ss");
  if (!isNewSignupStage && user.signupCompletedDate)
    return format(new Date(user.signupCompletedDate), "dd.MM.yyy HH:mm:ss");
  return "-";
};

export const TableRowCustom = withStyles((theme) => ({
  root: {
    "&:hover": {
      cursor: "pointer",
      background: "#ECEEFF",
    },
  },
}))(TableRow);

export const TableRowError = withStyles((theme) => ({
  root: {
    background: "#fee8e8",
    "&:hover": {
      cursor: "pointer",
      background: "#feeaea",
    },
  },
}))(TableRow);

export const TableRowErrorTimeout = withStyles((theme) => ({
  root: {
    background: "#dabce6",
    "&:hover": {
      cursor: "pointer",
      background: "#c4a9cf",
    },
  },
}))(TableRow);

function validateEmail(email: string) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}
export interface UserListpageProps extends RouteComponentProps<{}> {
  stage: UserStage;
}

type UserSearchFormik = {
  search: string;
  page: number;
  subscriptionPlanKey: SubscriptionPlanKey | string;
};

export const UserListPage = ({ path, stage }: UserListpageProps) => {
  const paginationClasses = PaginationStyles();
  const navigate = useNavigate();
  const [searchResults, setSearchResults] =
    useState<AdminUsersPaginatedResponse>();
  const [urlLoading, setUrlLoading] = useState(false);

  const isNewSignupStage = stage === UserStage.NewSignup;

  const location = useLocation();
  const searchParams = parse(location.search);

  const formik = useFormik<UserSearchFormik>({
    initialValues: {
      search: decodeURIComponent((searchParams.search as string) || ""),
      page: parseInt(searchParams.page as string) || 0,
      subscriptionPlanKey:
        (searchParams.subscriptionPlanKey as SubscriptionPlanKey) || "",
    },
    onSubmit: async (data) => {
      const queryParams: string[] = [];

      for (const [key, value] of Object.entries(data)) {
        //@ts-ignore
        if (data[key]) queryParams.push(`${key}=${encodeURIComponent(value)}`);
      }

      await navigate(`${path}?${queryParams.join("&")}`);

      const params = new AdminGetUsersQueryParams();
      params.skip = data.page * ROWS_PER_PAGE;
      params.take = ROWS_PER_PAGE;

      if (data.subscriptionPlanKey) {
        if (data.subscriptionPlanKey === NO_PLAN) {
          params.hasNoSubscriptionPlan = true;
        } else {
          params.subscriptionPlanKey =
            data.subscriptionPlanKey as SubscriptionPlanKey;
        }
      }

      params.stage = stage;

      const trimmedSearch = data.search.trim();

      if (validateEmail(trimmedSearch)) {
        params.email = trimmedSearch;
      } else {
        params.name = trimmedSearch;
      }

      setUrlLoading(true);

      try {
        const res = await Api.getUsers(params);

        setSearchResults(res.data);

        setUrlLoading(false);
      } catch {
        setUrlLoading(false);
      }
    },
    enableReinitialize: true,
  });

  // Fire search if form is populated initially
  useEffect(() => {
    formik.submitForm();
  }, []);

  const showSignupDate = showSignupDateCategory.includes(stage);

  return (
    <Layout title={stage}>
      <Box mb={2} component={Paper} padding={2}>
        <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <TextField
                fullWidth
                label="Search User"
                name="search"
                value={formik.values.search}
                onChange={(e) => {
                  formik.setFieldValue("page", 0);
                  formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
              />
            </Grid>
            <Grid item xs={2}>
              <InputLabel id="label">Plan</InputLabel>
              <Select
                fullWidth
                label="Plan"
                name="subscriptionPlanKey"
                value={formik.values.subscriptionPlanKey}
                onChange={(e) => {
                  formik.setFieldValue("page", 0);
                  formik.handleChange(e);
                  formik.submitForm();
                }}
                onBlur={formik.handleBlur}
              >
                {subscriptionPlanKey.map((st) => (
                  <MenuItem key={st.value} value={st.value}>
                    {st.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          <input type="submit" hidden />
        </form>
      </Box>
      <ComponentLoadingIndicator isLoading={urlLoading}>
        {searchResults && (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell style={{ paddingLeft: 15 }}>
                      Slug
                    </StyledTableCell>
                    <StyledTableCell>Name</StyledTableCell>
                    <StyledTableCell align="center">Id</StyledTableCell>
                    {showSignupDate && (
                      <StyledTableCell align="center">
                        Time left
                      </StyledTableCell>
                    )}
                    <StyledTableCell align="center">
                      {isNewSignupStage ? "Signup started date" : "Signup Date"}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <PaymentMethodTooltip>
                        <div>Payment</div>
                      </PaymentMethodTooltip>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <SubscriptionPlanTooltip>
                        <div>Subscription Plan</div>
                      </SubscriptionPlanTooltip>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      Subscription Status
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      Website count
                    </StyledTableCell>
                    <StyledTableCell align="center">Status</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.users.map((res) => {
                    const RowComponent =
                      res.crawlStatus === Status.CrawlError
                        ? TableRowError
                        : res.crawlStatus === Status.CrawlTimeout
                        ? TableRowErrorTimeout
                        : TableRowCustom;

                    return (
                      <RowComponent
                        key={res.slug}
                        onClick={(e) => {
                          if (
                            //@ts-ignore
                            !window.getSelection().toString().length &&
                            //@ts-ignore
                            e.target.tagName !== "A"
                          )
                            navigate(`/user/${res.slug}`);
                        }}
                      >
                        <StyledTableCell
                          component="td"
                          scope="row"
                          style={{ paddingLeft: 15 }}
                        >
                          <Link href={`/user/${res.slug}`}>{res.slug}</Link>
                        </StyledTableCell>
                        <StyledTableCell component="td" scope="row">
                          {`${res.firstName} ${res.lastName}`}
                        </StyledTableCell>
                        <StyledTableCell
                          align="center"
                          component="td"
                          scope="row"
                        >
                          {IdProvider.toUserId(res.id)}
                        </StyledTableCell>
                        {showSignupDate && (
                          <StyledTableCell
                            align="center"
                            component="td"
                            scope="row"
                          >
                            {res.signupCompletedDate
                              ? formatDistance(
                                  addDays(new Date(res.signupCompletedDate), 2),
                                  new Date(),
                                  { addSuffix: true }
                                )
                              : "	<Signup not finished>"}
                          </StyledTableCell>
                        )}
                        <StyledTableCell
                          align="center"
                          component="td"
                          scope="row"
                        >
                          {getShownDate(isNewSignupStage, res)}
                        </StyledTableCell>
                        <StyledTableCell
                          align="center"
                          component="td"
                          scope="row"
                        >
                          {getPaymentLabel(res?.paymentType)}
                        </StyledTableCell>
                        <StyledTableCell
                          align="center"
                          component="td"
                          scope="row"
                        >
                          {getPlanLabel(res?.subscriptionPlanKey)}
                        </StyledTableCell>
                        <StyledTableCell
                          align="center"
                          component="td"
                          scope="row"
                        >
                          {res?.subscriptionStatus}
                        </StyledTableCell>
                        <StyledTableCell
                          align="center"
                          component="td"
                          scope="row"
                        >
                          {res.websiteCount}
                        </StyledTableCell>
                        <StyledTableCell
                          align="center"
                          component="td"
                          scope="row"
                        >
                          {res.crawlStatus}
                        </StyledTableCell>
                      </RowComponent>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box mt={2} className={paginationClasses.root}>
              <TablePagination
                component="div"
                count={searchResults.count}
                page={formik.values.page}
                onChangePage={(_, page) => {
                  formik.setFieldValue("page", page);
                  formik.submitForm();
                }}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[]}
              />
            </Box>
          </>
        )}
      </ComponentLoadingIndicator>
    </Layout>
  );
};
